import { useEffect, useRef, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Bell, Loader2, Search, Upload, Megaphone, X, Trash2 } from "lucide-react";
import { useFileUpload } from "@/lib/uploadProgress";

function merchantDomain(t) {
  const d = (t.domain || "").replace(/^www\./, "");
  if (d) return d;
  return t.slug ? `/${t.slug}` : "";
}

export default function SalesNotices() {
  const uploadFile = useFileUpload();
  const [addons, setAddons] = useState([]);
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("addon");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("Unlock in App Store");
  const [image, setImage] = useState("");
  const [addonId, setAddonId] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [audience, setAudience] = useState("all");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [openHits, setOpenHits] = useState(false);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const searchBox = useRef(null);

  const load = () => {
    api.get("/platform/notices").then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(extractErr(e)));
    api.get("/platform/addons").then(({ data }) => setAddons((data.items || data || []).filter((a) => a.id && a.id !== "all_addons"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (audience !== "selected") {
      setHits([]);
      setOpenHits(false);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/platform/addons/bundle/tenants", { params: { q: q.trim() } })
        .then(({ data }) => { if (alive) setHits(data.items || []); })
        .catch((e) => { if (alive) toast.error(extractErr(e)); })
        .finally(() => { if (alive) setSearching(false); });
    }, q.trim() ? 80 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q, audience]);

  useEffect(() => {
    if (!openHits) return;
    const onDoc = (e) => {
      if (searchBox.current && !searchBox.current.contains(e.target)) setOpenHits(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openHits]);

  const toggle = (t) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[t.id]) delete next[t.id];
      else next[t.id] = t;
      return next;
    });
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const data = await uploadFile("/platform/notices/upload", fd);
      setImage(data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setUploading(false); }
  };

  const send = async () => {
    setSaving(true);
    try {
      await api.post("/platform/notices", {
        kind,
        title,
        body,
        cta_label: cta,
        image_url: image || null,
        addon_id: kind === "addon" ? addonId : null,
        link_url: kind === "link" ? linkUrl : null,
        audience,
        tenant_ids: audience === "selected" ? Object.keys(selected) : [],
      });
      toast.success("Popup sent. Merchants will see it when they open the dashboard.");
      setTitle(""); setBody(""); setImage(""); setLinkUrl(""); setSelected({});
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const picked = Object.values(selected);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-100 flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-teal-300" /> Merchant popups
        </h1>
        <p className="text-slate-400 text-sm mt-1">Push a beautiful dashboard popup to every store, a group, or one merchant. They can dismiss it anytime.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-4 text-slate-100" data-testid="notice-composer">
        <div className="flex gap-2">
          <button type="button" data-testid="notice-kind-addon" onClick={() => { setKind("addon"); setCta("Unlock in App Store"); }} className={`px-3 py-2 rounded-lg text-sm border ${kind === "addon" ? "bg-teal-500/20 border-teal-400 text-teal-100" : "border-slate-700 text-slate-300"}`}>Promote an addon</button>
          <button type="button" data-testid="notice-kind-link" onClick={() => { setKind("link"); setCta("Open link"); }} className={`px-3 py-2 rounded-lg text-sm border ${kind === "link" ? "bg-teal-500/20 border-teal-400 text-teal-100" : "border-slate-700 text-slate-300"}`}>Custom link</button>
        </div>
        <div>
          <Label className="text-slate-300">Headline</Label>
          <Input className="mt-1 bg-slate-950 border-slate-700" data-testid="notice-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Unlock Multiple Currency" />
        </div>
        <div>
          <Label className="text-slate-300">Message</Label>
          <Textarea rows={4} className="mt-1 bg-slate-950 border-slate-700" data-testid="notice-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Show INR vs USD prices automatically…" />
        </div>
        <div>
          <Label className="text-slate-300">Button text</Label>
          <Input className="mt-1 bg-slate-950 border-slate-700" data-testid="notice-cta" value={cta} onChange={(e) => setCta(e.target.value)} />
        </div>
        <div>
          <Label className="text-slate-300">Image</Label>
          <div className="mt-2 flex items-center gap-3">
            {image && <img src={mediaUrl(image)} alt="" className="h-16 w-24 rounded-lg object-cover border border-slate-700" />}
            <label className="inline-flex items-center gap-2 text-sm text-teal-300 cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload image"}
              <input type="file" hidden accept="image/*" data-testid="notice-image" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
        </div>
        {kind === "addon" ? (
          <div>
            <Label className="text-slate-300">Addon to open in App Store</Label>
            <select className="mt-1 w-full h-10 rounded-md bg-slate-950 border border-slate-700 px-3 text-sm" data-testid="notice-addon" value={addonId} onChange={(e) => setAddonId(e.target.value)}>
              <option value="">Select addon…</option>
              {addons.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <Label className="text-slate-300">Link</Label>
            <Input className="mt-1 bg-slate-950 border-slate-700" data-testid="notice-link" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://… or /admin/billing" />
          </div>
        )}
        <div>
          <Label className="text-slate-300">Who should see this?</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" data-testid="notice-audience-all" onClick={() => setAudience("all")} className={`px-3 py-2 rounded-lg text-sm border ${audience === "all" ? "bg-teal-500/20 border-teal-400" : "border-slate-700"}`}>All merchants</button>
            <button type="button" data-testid="notice-audience-selected" onClick={() => { setAudience("selected"); setOpenHits(true); }} className={`px-3 py-2 rounded-lg text-sm border ${audience === "selected" ? "bg-teal-500/20 border-teal-400" : "border-slate-700"}`}>Selected merchants / group</button>
          </div>
        </div>
        {audience === "selected" && (
          <div className="space-y-2">
            <div className="relative" ref={searchBox}>
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setOpenHits(true); }}
                onFocus={() => setOpenHits(true)}
                placeholder="Search by name, email, or domain…"
                className="pl-9 bg-slate-950 border-slate-700"
                data-testid="notice-tenant-search"
                autoComplete="off"
              />
              {openHits && (
                <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 shadow-xl" data-testid="notice-tenant-list">
                  {searching && (
                    <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                    </div>
                  )}
                  {!searching && hits.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500">No merchants match that name, email, or domain.</div>
                  )}
                  {hits.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => toggle(t)}
                      className={`w-full text-left px-3 py-2 border-t border-slate-800 first:border-t-0 ${selected[t.id] ? "bg-teal-500/10" : "hover:bg-slate-900"}`}
                      data-testid={`notice-hit-${t.slug}`}
                    >
                      <div className="text-sm text-slate-100 truncate">{t.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {t.email || "no email"}
                        <span className="text-slate-600"> · </span>
                        <span className="font-mono-nums">{merchantDomain(t)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="notice-selected-chips">
                {picked.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 px-2 py-0.5 text-xs">
                    {t.name}
                    <button type="button" onClick={() => toggle(t)} className="text-slate-400 hover:text-slate-100" aria-label={`Remove ${t.name}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <Button onClick={send} disabled={saving} className="bg-teal-600 hover:bg-teal-500" data-testid="notice-send">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />} Send popup
        </Button>
      </Card>

      <Card className="bg-slate-900 border-slate-800 p-6 space-y-3 text-slate-100">
        <div className="font-semibold">Recent popups</div>
        {items.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : items.map((n) => (
          <div key={n.id} className="flex flex-wrap items-center justify-between gap-2 py-3 border-t border-slate-800" data-testid={`notice-row-${n.id}`}>
            <div>
              <div className="text-sm font-medium">{n.title}</div>
              <div className="text-xs text-slate-500">{n.kind} · {n.audience}{n.addon_id ? ` · ${n.addon_id}` : ""}</div>
            </div>
            <div className="flex items-center gap-2">
              {n.active ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Live</Badge> : <Badge className="bg-slate-700/40">Off</Badge>}
              <Button size="sm" variant="outline" className="border-slate-600 text-slate-200" onClick={async () => { await api.patch(`/platform/notices/${n.id}`, { active: !n.active }); load(); }}>{n.active ? "Turn off" : "Turn on"}</Button>
              <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`notice-delete-${n.id}`} onClick={async () => {
                if (!window.confirm("Delete this popup? Merchants will no longer see it.")) return;
                try {
                  await api.delete(`/platform/notices/${n.id}`);
                  toast.success("Popup deleted");
                  load();
                } catch (e) { toast.error(extractErr(e)); }
              }}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
